<template>
  <div class="info-modal" role="presentation" @mousedown.self="$emit('close')">
    <section
      ref="dialog"
      class="info-modal__dialog"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      tabindex="-1"
    >
      <button ref="close" type="button" class="info-modal__close" :aria-label="copy.close" @click="$emit('close')">×</button>

      <header class="info-modal__header">
        <div class="info-modal__icon">
          <PerkIcon v-if="type === 'perk'" :perk="entry" :side="side" />
          <GameIcon v-else :src="entry.icon" :alt="name" :rarity="entry.rarity" :kind="type" />
        </div>
        <div>
          <span>{{ typeLabel }}</span>
          <h2 :id="titleId">{{ name }}</h2>
          <small v-if="entry.rarity">{{ rarityLabel }}</small>
        </div>
      </header>

      <div class="info-modal__body">
        <section>
          <h3>{{ copy.official }}</h3>
          <p v-if="profile.description" class="info-modal__description">{{ profile.description }}</p>
          <p v-else class="info-modal__empty">{{ copy.structuredFallback }}</p>
        </section>

        <section v-if="profile.mechanics.length">
          <h3>{{ copy.mechanics }}</h3>
          <dl class="info-modal__mechanics">
            <div v-for="row in profile.mechanics" :key="row.key">
              <dt>{{ row.label }}</dt>
              <dd>{{ row.value }}</dd>
            </div>
          </dl>
        </section>

        <section v-if="profile.analysis.length">
          <h3>{{ copy.analysis }}</h3>
          <div class="info-modal__analysis">
            <article v-for="insight in profile.analysis" :key="insight.metricId" :class="{ 'is-requirement-gap': insight.requirementGap, 'is-critical-gap': insight.criticalGap }">
              <header>
                <strong>{{ insight.label }} &middot; {{ insight.value }}</strong>
                <span>{{ insight.priorityLabel }} &middot; {{ insight.status }} &middot; {{ insight.utility }}/100</span>
              </header>
              <div><i :style="{ width: insight.utility + '%' }"></i></div>
              <p>{{ insight.note }}</p>
            </article>
          </div>
        </section>

        <section class="info-modal__scoring">
          <h3>{{ copy.scoring }}</h3>
          <p>{{ profile.scoringNote }}</p>
        </section>
      </div>

      <footer>
        <a :href="profile.sourceUrl" target="_blank" rel="noopener noreferrer">{{ copy.source }}</a>
        <span>{{ copy.snapshot }}</span>
      </footer>
    </section>
  </div>
</template>

<script>
import GameIcon from '@/components/GameIcon.vue'
import PerkIcon from '@/components/PerkIcon.vue'
import { officialEntryProfile } from '@/lib/loadoutOfficialDescriptions'
import { localizedEntryName } from '@/lib/loadoutLocalization'

const COPY = {
  fr: {
    close: 'Fermer la fiche',
    official: 'Description officielle',
    mechanics: 'Statistiques officielles',
    analysis: 'Valeur pratique dans ce package',
    scoring: 'Lecture par la roulette',
    source: 'Ouvrir la fiche du Wiki officiel',
    snapshot: 'Données locales · aucun appel externe',
    structuredFallback: 'Le snapshot officiel ne fournit pas de texte narratif pour cette entrée ; les statistiques structurées ci-dessous constituent la référence disponible.',
    types: { perk: 'Perk', item: 'Item', addon: 'Add-on', offering: 'Offrande' }
  },
  en: {
    close: 'Close details',
    official: 'Official description',
    mechanics: 'Official statistics',
    analysis: 'Practical value in this package',
    scoring: 'How the roulette reads it',
    source: 'Open on the official Wiki',
    snapshot: 'Local data · no external request',
    structuredFallback: 'The official snapshot has no narrative text for this entry; the structured statistics below are the available reference.',
    types: { perk: 'Perk', item: 'Item', addon: 'Add-on', offering: 'Offering' }
  },
  de: {
    close: 'Details schließen',
    official: 'Offizielle Beschreibung',
    mechanics: 'Offizielle Werte',
    analysis: 'Praktischer Wert in diesem Paket',
    scoring: 'Bewertung durch das Roulette',
    source: 'Im offiziellen Wiki öffnen',
    snapshot: 'Lokale Daten · keine externe Anfrage',
    structuredFallback: 'Für diesen Eintrag ist kein Beschreibungstext verfügbar; die strukturierten Werte unten sind die verfügbare Referenz.',
    types: { perk: 'Perk', item: 'Gegenstand', addon: 'Add-on', offering: 'Opfergabe' }
  },
  es: {
    close: 'Cerrar detalles',
    official: 'Descripción oficial',
    mechanics: 'Estadísticas oficiales',
    analysis: 'Valor práctico en este paquete',
    scoring: 'Lectura de la ruleta',
    source: 'Abrir en la Wiki oficial',
    snapshot: 'Datos locales · sin solicitud externa',
    structuredFallback: 'No hay texto narrativo para esta entrada; las estadísticas estructuradas siguientes son la referencia disponible.',
    types: { perk: 'Ventaja', item: 'Objeto', addon: 'Accesorio', offering: 'Ofrenda' }
  },
  ja: {
    close: '詳細を閉じる',
    official: '公式説明',
    mechanics: '公式数値',
    analysis: 'このパッケージでの実用価値',
    scoring: 'ルーレットでの評価',
    source: '公式Wikiで開く',
    snapshot: 'ローカルデータ · 外部通信なし',
    structuredFallback: 'この項目の説明文はありません。以下の構造化された数値を参照してください。',
    types: { perk: 'パーク', item: 'アイテム', addon: 'アドオン', offering: 'オファリング' }
  }
}

export default {
  name: 'LoadoutInfoModal',
  components: { GameIcon, PerkIcon },
  props: {
    entry: { type: Object, required: true },
    type: { type: String, required: true },
    side: { type: String, required: true }
  },
  computed: {
    language () {
      return String(this.$i18n.locale || 'en').split('-')[0]
    },
    copy () {
      return COPY[this.language] || COPY.en
    },
    name () {
      return localizedEntryName(this.entry, this.type, this.$i18n.locale)
    },
    typeLabel () {
      return this.copy.types[this.type] || this.type
    },
    rarityLabel () {
      const keys = { 1: 'common', 2: 'uncommon', 3: 'rare', 4: 'veryRare', 5: 'visceral', 8: 'event', 11: 'limited' }
      const key = `forge.rarity.${keys[this.entry.rarity] || 'special'}`
      return this.$te(key) ? this.$t(key) : ''
    },
    profile () {
      return officialEntryProfile(this.entry, this.type, this.side, this.$i18n.locale)
    },
    titleId () {
      return `loadout-info-${this.type}-${this.entry.id}`
    }
  },
  mounted () {
    this.previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', this.onKeydown)
    this.$nextTick(() => this.$refs.close && this.$refs.close.focus())
  },
  beforeDestroy () {
    document.body.style.overflow = this.previousOverflow || ''
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    onKeydown (event) {
      if (event.key === 'Escape') this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
.info-modal {
  position: fixed;
  z-index: 2500;
  inset: 0;
  padding: 24px;
  background: rgba(1, 4, 6, .82);
  backdrop-filter: blur(12px);
  display: grid;
  place-items: center;
}
.info-modal__dialog {
  width: min(720px, 100%);
  max-height: min(820px, calc(100vh - 48px));
  border: 1px solid rgba(104, 220, 255, .22);
  border-radius: 24px;
  background: linear-gradient(145deg, #141a1e, #080b0d 68%);
  box-shadow: 0 32px 120px rgba(0, 0, 0, .65);
  color: #f4f3ed;
  overflow: auto;
  position: relative;
}
.info-modal__close {
  width: 38px;
  height: 38px;
  border: 1px solid rgba(255,255,255,.13);
  border-radius: 11px;
  background: rgba(4,7,9,.82);
  color: #fff;
  cursor: pointer;
  font-size: 25px;
  line-height: 1;
  position: absolute;
  right: 18px;
  top: 18px;
}
.info-modal__close:hover, .info-modal__close:focus-visible { border-color: #68dcff; outline: none; }
.info-modal__header {
  min-height: 150px;
  padding: 24px 70px 22px 24px;
  border-bottom: 1px solid rgba(255,255,255,.08);
  background: radial-gradient(circle at 15% 30%, rgba(104,220,255,.12), transparent 30%);
  display: flex;
  align-items: center;
  gap: 22px;
}
.info-modal__icon { width: 112px; height: 112px; flex: 0 0 112px; display: grid; place-items: center; }
.info-modal__icon > * { transform: scale(.9); }
.info-modal__header span, .info-modal__header small {
  color: #68dcff;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: .14em;
  text-transform: uppercase;
}
.info-modal__header h2 { margin: 7px 0 9px; font-size: clamp(25px, 5vw, 39px); letter-spacing: -.04em; line-height: 1; }
.info-modal__header small { color: rgba(255,255,255,.48); }
.info-modal__body { padding: 24px; display: grid; gap: 22px; }
.info-modal__body section { min-width: 0; }
.info-modal__body h3 { margin: 0 0 10px; color: rgba(255,255,255,.48); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; }
.info-modal__description, .info-modal__empty, .info-modal__scoring p { margin: 0; color: rgba(255,255,255,.84); font-size: 14px; line-height: 1.72; white-space: pre-line; }
.info-modal__empty { color: rgba(255,255,255,.58); }
.info-modal__mechanics { margin: 0; border: 1px solid rgba(255,255,255,.07); border-radius: 15px; overflow: hidden; }
.info-modal__mechanics div { padding: 10px 13px; border-bottom: 1px solid rgba(255,255,255,.055); display: grid; grid-template-columns: minmax(120px, .75fr) minmax(0, 1.25fr); gap: 14px; }
.info-modal__mechanics div:last-child { border-bottom: 0; }
.info-modal__mechanics dt { color: rgba(255,255,255,.46); font-size: 11px; }
.info-modal__mechanics dd { margin: 0; color: #fff; font-size: 12px; font-weight: 800; overflow-wrap: anywhere; }
.info-modal__analysis { display: grid; gap: 8px; }
.info-modal__analysis article { padding: 11px 12px; border: 1px solid rgba(255,255,255,.07); border-radius: 12px; background: rgba(255,255,255,.025); }
.info-modal__analysis article.is-requirement-gap { border-color: rgba(255,184,92,.32); background: rgba(255,153,55,.055); }
.info-modal__analysis article.is-critical-gap { border-color: rgba(255,100,100,.42); background: rgba(255,78,78,.075); }
.info-modal__analysis article.is-critical-gap span { color: #ff8d8d; }
.info-modal__analysis header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.info-modal__analysis strong { color: #f4f3ed; font-size: 11px; }
.info-modal__analysis span { color: #82e8ba; font-size: 9px; font-weight: 850; text-transform: uppercase; }
.info-modal__analysis article > div { height: 3px; margin: 8px 0; border-radius: 99px; background: rgba(255,255,255,.07); overflow: hidden; }
.info-modal__analysis i { height: 100%; border-radius: inherit; background: linear-gradient(90deg, #68dcff, #82e8ba); display: block; }
.info-modal__analysis article.is-critical-gap i { background: linear-gradient(90deg, #ff765f, #ffad78); }
.info-modal__analysis p { margin: 0; color: rgba(255,255,255,.48); font-size: 10px; line-height: 1.5; }
.info-modal__scoring { padding: 15px; border: 1px solid rgba(104,220,255,.16); border-radius: 15px; background: rgba(104,220,255,.06); }
.info-modal__scoring p { color: rgba(225,247,255,.78); font-size: 12px; }
.info-modal footer { padding: 16px 24px 20px; border-top: 1px solid rgba(255,255,255,.07); display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.info-modal footer a { color: #7ce4ff; font-size: 12px; font-weight: 850; text-decoration: none; }
.info-modal footer a:hover { text-decoration: underline; }
.info-modal footer span { color: rgba(255,255,255,.32); font-size: 9px; letter-spacing: .08em; text-transform: uppercase; }
@media (max-width: 560px) {
  .info-modal { padding: 10px; }
  .info-modal__dialog { max-height: calc(100vh - 20px); border-radius: 18px; }
  .info-modal__header { min-height: 125px; padding: 18px 56px 18px 16px; gap: 12px; }
  .info-modal__icon { width: 82px; height: 82px; flex-basis: 82px; }
  .info-modal__icon > * { transform: scale(.68); }
  .info-modal__body { padding: 18px 16px; }
  .info-modal__mechanics div { grid-template-columns: 1fr; gap: 4px; }
  .info-modal footer { align-items: flex-start; flex-direction: column; padding: 14px 16px 18px; }
}
</style>
